<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
  >
    <template #button-content>
      <feather-icon
        :badge="unreadCount > 0 ? unreadCount : null"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          Notificações
        </h4>
        <b-badge
          v-if="unreadCount > 0"
          pill
          variant="light-primary"
        >
          {{ unreadCount }} Nova{{ unreadCount > 1 ? 's' : '' }}
        </b-badge>
      </div>
    </li>

    <!-- Notifications List -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <!-- Loading State -->
      <div
        v-if="loading"
        class="media d-flex align-items-center justify-content-center p-3"
      >
        <b-spinner small />
        <span class="ml-2">Carregando...</span>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="notificacoes.length === 0"
        class="media d-flex align-items-center justify-content-center p-3"
      >
        <small class="text-muted">Nenhuma notificação</small>
      </div>

      <!-- Notifications from API -->
      <b-link
        v-for="notification in notificacoes"
        :key="notification.id"
        @click="handleNotificationClick(notification)"
      >
        <b-media>
          <template #aside>
            <b-avatar
              size="32"
              :variant="getNotificationVariant(notification)"
            >
              <feather-icon
                :icon="getNotificationIcon(notification)"
                size="18"
              />
            </b-avatar>
          </template>
          <p class="media-heading">
            <span class="font-weight-bolder">
              {{ getNotificationTitle(notification) }}
            </span>
            <small
              v-if="!notification.read_at"
              class="badge badge-primary ml-1"
            >
              Nova
            </small>
          </p>
          <small class="notification-text">
            {{ getNotificationMessage(notification) }}
          </small>
          <small class="text-muted d-block">
            {{ formatDate(notification.created_at) }}
          </small>
        </b-media>
      </b-link>
    </vue-perfect-scrollbar>

    <!-- Footer -->
    <li class="dropdown-menu-footer">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
        size="sm"
        :disabled="notificacoes.length === 0 || loading"
        @click="marcarTodasComoLidas"
      >
        Marcar todas como lidas
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton, BSpinner,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Ripple from 'vue-ripple-directive'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import notificacoesMixin from '@/mixins/notificacoes'

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    BButton,
    BSpinner,
    VuePerfectScrollbar,
  },
  directives: {
    Ripple,
  },
  mixins: [notificacoesMixin],
  data() {
    return {
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
    }
  },
  methods: {
    /**
     * Extrai título da notificação baseado na estrutura
     * Notificações vêm com dados nested em notification.data
     */
    getNotificationTitle(notification) {
      if (notification.data?.tipo) {
        return `${notification.data.tipo} - Agendamento`
      }
      return notification.type || 'Notificação'
    },

    /**
     * Extrai mensagem da notificação
     */
    getNotificationMessage(notification) {
      if (notification.data?.descricao) {
        return notification.data.descricao
      }

      // Construir mensagem a partir dos dados disponíveis
      const data = notification.data || {}
      const cliente = data.cliente_id ? `Cliente: ${data.cliente_id}` : ''
      const status = data.status ? `Status: ${data.status}` : ''
      const tipo = data.tipo || ''

      const partes = [tipo, cliente, status].filter(Boolean)
      return partes.join(' - ') || notification.message || 'Sem descrição'
    },

    /**
     * Retorna a variante de cor baseada no tipo de notificação
     */
    getNotificationVariant(notification) {
      const tipo = notification.data?.tipo?.toLowerCase() || 'agendamento'

      const variants = {
        contato: 'light-info',
        ligacao: 'light-info',
        email: 'light-primary',
        sms: 'light-warning',
        agendamento: 'light-info',
        alerta: 'light-warning',
        erro: 'light-danger',
        sucesso: 'light-success',
      }
      return variants[tipo] || 'light-secondary'
    },

    /**
     * Retorna o ícone baseado no tipo de notificação
     */
    getNotificationIcon(notification) {
      const tipo = notification.data?.tipo?.toLowerCase() || 'agendamento'

      const icons = {
        contato: 'PhoneIcon',
        ligacao: 'PhoneIcon',
        email: 'MailIcon',
        sms: 'MessageSquareIcon',
        agendamento: 'CalendarIcon',
        alerta: 'AlertTriangleIcon',
        erro: 'XIcon',
        sucesso: 'CheckIcon',
      }
      return icons[tipo] || 'BellIcon'
    },

    /**
     * Formata a data da notificação em tempo relativo
     */
    formatDate(date) {
      if (!date) return ''
      try {
        return formatDistanceToNow(new Date(date), {
          addSuffix: true,
          locale: ptBR,
        })
      } catch {
        return date
      }
    },

    /**
     * Manipula clique em notificação
     */
    async handleNotificationClick(notification) {
      if (!notification.read_at) {
        await this.marcarComoLida(notification.id)
      }
      // Aqui pode-se adicionar navegação ou outras ações
    },
  },
}
</script>

<style scoped>
.dropdown-notification {
  cursor: pointer;
}

.media-heading {
  display: flex;
  align-items: center;
}

.badge {
  font-size: 0.6rem;
  padding: 0.25rem 0.5rem;
  margin-left: 0.5rem;
}
</style>
